import { useCallback, useMemo, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { BoltIcon } from '@heroicons/react/24/outline';
import type { ChatDoc } from '@odo/shared-ui';
import { useConversationQuickReplies } from '../../../hooks/useConversationQuickReplies';
import { useMyPermissions } from '../../../hooks/useMyPermissions';
import { usePatientCard } from '../../../hooks/usePatientCard';
import type { QuickReplyChatType } from '../../../services/conversationQuickReplies';
import { apiErrorMessage } from '../../../utils/apiError';
import { QuickRepliesPopover } from '../QuickRepliesPopover';
import type { QuickReplyItem } from '../quickReplyItem';
import { QuickRepliesManageModal } from '../admin/QuickRepliesManageModal';
import { fillQuickReplyTokens } from './fillQuickReplyTokens';
import { quickReplyDocText } from './quickReplyDocText';

interface ChatQuickReplyItem extends QuickReplyItem {
    bodyDoc: ChatDoc;
}

interface ChatQuickRepliesProps {
    patientId: string;
    chatType: QuickReplyChatType;
    // The composer's top edge: the popover opens above it at its width so it fits narrow panels.
    anchorRef: RefObject<HTMLElement>;
    onInsert: (content: ChatDoc[]) => void;
}

export function ChatQuickReplies({
    patientId,
    chatType,
    anchorRef,
    onInsert
}: ChatQuickRepliesProps) {
    const [open, setOpen] = useState(false);
    const [manageOpen, setManageOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const close = useCallback(() => setOpen(false), []);

    const handlePick = useCallback(
        (content: ChatDoc[]) => {
            close();
            onInsert(content);
        },
        [close, onInsert]
    );

    const openManage = useCallback(() => {
        close();
        setManageOpen(true);
    }, [close]);

    const closeManage = useCallback(() => {
        setManageOpen(false);
        buttonRef.current?.focus();
    }, []);

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                title="Quick replies"
                aria-label="Quick replies"
                aria-expanded={open}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${
                    open
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                }`}
            >
                <BoltIcon className="w-3.5 h-3.5" />
            </button>
            {open && (
                <ChatQuickRepliesPopover
                    patientId={patientId}
                    chatType={chatType}
                    anchorRef={anchorRef}
                    buttonRef={buttonRef}
                    onPick={handlePick}
                    onClose={close}
                    onManage={openManage}
                />
            )}
            {manageOpen &&
                createPortal(<QuickRepliesManageModal onClose={closeManage} />, document.body)}
        </>
    );
}

interface ChatQuickRepliesPopoverProps {
    patientId: string;
    chatType: QuickReplyChatType;
    anchorRef: RefObject<HTMLElement>;
    buttonRef: RefObject<HTMLButtonElement>;
    onPick: (content: ChatDoc[]) => void;
    onClose: () => void;
    onManage: () => void;
}

// Mounted only while open, so the list and patient card load on first use.
function ChatQuickRepliesPopover({
    patientId,
    chatType,
    anchorRef,
    buttonRef,
    onPick,
    onClose,
    onManage
}: ChatQuickRepliesPopoverProps) {
    const replies = useConversationQuickReplies({ chatType });
    const { data: card } = usePatientCard(patientId);
    const { hasPermission } = useMyPermissions();

    const items = useMemo<ChatQuickReplyItem[]>(
        () =>
            replies.data.map((reply) => ({
                key: reply.id,
                title: reply.title,
                content: quickReplyDocText(reply.bodyDoc),
                code: reply.code,
                group: reply.category,
                bodyDoc: reply.bodyDoc
            })),
        [replies.data]
    );

    const handlePick = (item: ChatQuickReplyItem) => {
        const filled = fillQuickReplyTokens(item.bodyDoc, {
            doctor_last_name: card?.practice?.drLastName,
            doctor_first_name: card?.practice?.drFirstName,
            patient_name: card?.name,
            patient_first_name: card?.firstName
        });
        onPick(filled.content ?? []);
    };

    return (
        <QuickRepliesPopover
            anchorRef={anchorRef}
            buttonRef={buttonRef}
            replies={items}
            isFetching={replies.isFetching}
            errorMessage={
                replies.error
                    ? apiErrorMessage(replies.error, 'Could not load quick replies.')
                    : null
            }
            onRetry={() => void replies.refetch()}
            onPick={handlePick}
            onClose={onClose}
            footer={
                hasPermission('resources.update') && (
                    <div className="flex-shrink-0 border-t border-gray-200 px-3 py-2">
                        <button
                            type="button"
                            onClick={onManage}
                            className="text-xs font-semibold text-gray-500 hover:text-gray-800"
                        >
                            Manage quick replies
                        </button>
                    </div>
                )
            }
        />
    );
}
